

import { CreditCard, Receipt, CheckCircle, Wallet, FileText, Download, Pencil } from "lucide-react";
import { formatCurrencyFull, formatDate } from "@/lib/data";
import { getImageUrl } from "@/lib/utils";
import { ClientPayment, CustomerFinancials } from "./types";
import { ReceiptButton } from "./PaymentReceipt";

interface UnifiedPaymentSectionProps {
  customer: CustomerFinancials | undefined;
  payments: ClientPayment[] | undefined;
  onAddPayment?: () => void;
  /** When true (admin users), shows an Edit control on each recorded payment. */
  canEditPayments?: boolean;
  onEditPayment?: (payment: ClientPayment) => void;
  event?: {
    id: number;
    name: string;
    start_date: string;
    venue?: { name: string } | null;
    client?: { name: string; phone?: string; email?: string } | null;
  };
}

export default function UnifiedPaymentSection({
  customer,
  payments,
  onAddPayment,
  canEditPayments = false,
  onEditPayment,
  event,
}: UnifiedPaymentSectionProps) {
  // Calculate paid amount from payments array (more reliable than customer.paid)
  const calculatedPaid = (payments || []).reduce(
    (sum, payment) => sum + (Number(payment.amount) || 0),
    0
  );
  
  const safeCustomer = customer || {
    subtotal: 0,
    discount: 0,
    tax: 0,
    total: 0,
    paid: 0,
    balance: 0,
  };
  
  // Use calculated paid amount
  const actualPaid = calculatedPaid || safeCustomer.paid || 0;
  const actualBalance = safeCustomer.total - actualPaid;
  
  const safePayments = payments || [];
  const hasBalance = actualBalance > 0;
  const isFullyPaid = !hasBalance && safeCustomer.total > 0;

  return (
    <div className="bg-white rounded-xl border border-stone-200 overflow-hidden shadow-sm">
      {/* Header */}
      <div className="px-4 py-3 bg-gradient-to-r from-amber-50 to-amber-100/50 border-b border-amber-100 flex items-center justify-between">
        <h3 className="font-bold text-stone-800 text-sm flex items-center gap-2">
          <CreditCard className="w-4 h-4 text-amber-600" />
          Customer Payments
        </h3>
        {hasBalance && onAddPayment && (
          <button
            onClick={onAddPayment}
            className="text-xs px-3 py-1.5 rounded-lg bg-emerald-500 text-white font-semibold hover:bg-emerald-600 transition-colors flex items-center gap-1 shadow-sm"
          >
            <Wallet className="w-3 h-3" />
            Take Payment
          </button>
        )}
        {isFullyPaid && (
          <span className="flex items-center gap-1 text-xs text-emerald-600 font-medium bg-emerald-50 px-2 py-1 rounded-full">
            <CheckCircle className="w-3 h-3" />
            Fully Paid
          </span>
        )}
      </div>

      {/* Summary Cards - Total | Paid | Balance */}
      <div className="grid grid-cols-3 divide-x divide-stone-100 border-b border-stone-100">
        <div className="p-3 text-center">
          <p className="text-[10px] text-stone-500 uppercase mb-1">Total Amount</p>
          <p className="text-sm font-bold text-stone-800">{formatCurrencyFull(safeCustomer.total)}</p>
        </div>
        <div className="p-3 text-center">
          <p className="text-[10px] text-stone-500 uppercase mb-1">Amount Paid</p>
          <p className="text-sm font-bold text-emerald-600">{formatCurrencyFull(actualPaid)}</p>
        </div>
        <div className={`p-3 text-center ${hasBalance ? 'bg-amber-50/50' : 'bg-emerald-50/50'}`}>
          <p className={`text-[10px] uppercase mb-1 ${hasBalance ? 'text-amber-600' : 'text-emerald-600'}`}>
            {hasBalance ? 'Balance Due' : 'Status'}
          </p>
          <p className={`text-sm font-bold ${hasBalance ? 'text-amber-700' : 'text-emerald-600'}`}>
            {hasBalance ? formatCurrencyFull(actualBalance) : 'Fully Paid'}
          </p>
        </div>
      </div>

      {/* Payment History List */}
      <div>
        <div className="px-4 py-2 bg-stone-50 border-b border-stone-100 flex items-center gap-2">
          <Receipt className="w-3.5 h-3.5 text-stone-400" />
          <span className="text-xs font-medium text-stone-600">
            Payment History ({safePayments.length})
          </span>
        </div>
        
        <div className="divide-y divide-stone-100 max-h-60 overflow-y-auto">
          {safePayments.length > 0 ? (
            safePayments.map((payment) => {
              // Get proof URL - handle both payment_proof_path and proof_path
              const proofPath = payment.payment_proof_path || (payment as any).proof_path;
              const hasProof = !!proofPath;
              const proofUrl = hasProof ? getImageUrl(proofPath) : null;
              
              return (
                <div key={payment.id} className="p-3 flex items-center justify-between hover:bg-stone-50/50">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
                      <CheckCircle className="w-4 h-4 text-emerald-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-stone-800 text-sm">
                        {formatCurrencyFull(payment.amount)}
                      </p>
                      <p className="text-xs text-stone-500 capitalize">
                        {payment.payment_method.replace(/_/g, ' ')}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-stone-500">{formatDate(payment.payment_date)}</p>
                    {payment.transaction_id && (
                      <p className="text-[10px] text-stone-400">Ref: {payment.transaction_id}</p>
                    )}
                    <div className="flex items-center justify-end gap-2 mt-1">
                      {event && (
                        <ReceiptButton
                          payment={payment}
                          event={event}
                        />
                      )}
                      {canEditPayments && onEditPayment && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onEditPayment(payment);
                          }}
                          className="inline-flex items-center gap-1 text-[10px] text-amber-600 hover:text-amber-700"
                          title="Edit payment amount (admin)"
                          aria-label="Edit payment amount"
                        >
                          <Pencil className="w-3 h-3" />
                          Edit
                        </button>
                      )}
                      {hasProof && proofUrl && (
                        <a
                          href={proofUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-[10px] text-blue-600 hover:text-blue-700"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Download className="w-3 h-3" />
                          Proof
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="p-6 text-center">
              <FileText className="w-10 h-10 text-stone-200 mx-auto mb-2" />
              <p className="text-sm text-stone-400">No payments recorded yet</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
